import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField, Select, MenuItem, CircularProgress, Box, Typography,
  RadioGroup, FormControlLabel, Radio, TextareaAutosize, Card, CardContent, Grid
} from '@mui/material';
import ContainerWithSidebar from '../../components/UI/containerWithSidebar/ContainerWithSidebar';
import { GeneralContainer, H1, H3, TextLink, Table } from '../../components/UI/generalLayout/Layout.styled';
import { Spacer } from '../../components/UI/spacer/spacer';
import { GenericSolidButton } from '../../components/UI/buttons/Button.styled';
import Colors from '../../constants/Colors';
import { NavegacaoService } from '../../core/http/service/NavegacaoService';
import moment from 'moment';
import useResponsiveInfo from '../../hooks/useResponsiveInfo';
import ErrorArea from '../../components/error/ErrorArea';

const AcompanhamentoNavegacao = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isMobile } = useResponsiveInfo();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [navDetails, setNavDetails] = useState<any>(null);
  const [errors, setErrors] = useState<any[]>([]);

  // Textarea collapse triggers
  const [showAddEvolucao, setShowAddEvolucao] = useState<boolean>(false);
  const [showAddPlano, setShowAddPlano] = useState<boolean>(false);
  const [showAddConduta, setShowAddConduta] = useState<boolean>(false);

  // Text inputs
  const [evolucaoText, setEvolucaoText] = useState<string>('');
  const [planoText, setPlanoText] = useState<string>('');
  const [condutaText, setCondutaText] = useState<string>('');

  // Submitting statuses
  const [isSubmittingAcomp, setIsSubmittingAcomp] = useState<boolean>(false);

  // Next steps form states
  const [nextStepOpcao, setNextStepOpcao] = useState<string>('Aprazamento');
  const [nextStepData, setNextStepData] = useState<string>(moment().format('YYYY-MM-DD'));
  const [nextStepTurno, setNextStepTurno] = useState<string>('');
  const [nextStepMotivoAlta, setNextStepMotivoAlta] = useState<string>('');
  const [isSubmittingNextStep, setIsSubmittingNextStep] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const service = new NavegacaoService();
      const res: any = await service.getDetalhes(id);
      setNavDetails(res?.data?.data || null);
      if (res?.data?.data) {
        setNextStepOpcao(res.data.data.ativo ? 'Aprazamento' : 'Alta');
        setNextStepData(res.data.data.dataAprazamento || moment().format('YYYY-MM-DD'));
        setNextStepTurno(res.data.data.turnoAprazamento || '');
      }
    } catch (err) {
      console.error(err);
      setErrors([{ message: 'Não foi possível carregar os detalhes do acompanhamento.' }]);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAlterarRisco = async (novoRisco: string) => {
    if (!id) return;
    setErrors([]);
    try {
      const service = new NavegacaoService();
      await service.alterarRisco({ navegacaoId: id, novoRisco });
      await loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] || 'Falha ao alterar risco.';
      setErrors([{ message: msg }]);
    }
  };

  const handleSalvarAcompanhamento = async (tipo: number) => {
    const text = tipo === 1 ? evolucaoText : tipo === 2 ? planoText : condutaText;
    if (!id || !text.trim()) return;

    setErrors([]);
    setIsSubmittingAcomp(true);
    try {
      const service = new NavegacaoService();
      await service.adicionarAcompanhamento({
        navegacaoId: id,
        tipo,
        texto: text.trim()
      });
      
      if (tipo === 1) {
        setEvolucaoText('');
        setShowAddEvolucao(false);
      } else if (tipo === 2) {
        setPlanoText('');
        setShowAddPlano(false);
      } else {
        setCondutaText('');
        setShowAddConduta(false);
      }

      await loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] || 'Falha ao adicionar registro.';
      setErrors([{ message: msg }]);
    } finally {
      setIsSubmittingAcomp(false);
    }
  };

  const handleSalvarProximaEtapa = async () => {
    if (!id) return;
    setErrors([]);

    if (nextStepOpcao === 'Aprazamento') {
      if (!nextStepData || !nextStepTurno) {
        setErrors([{ message: 'Data e Turno do Aprazamento são obrigatórios.' }]);
        return;
      }
      const selected = moment(nextStepData, 'YYYY-MM-DD');
      const today = moment().startOf('day');
      if (selected.isBefore(today)) {
        setErrors([{ message: 'A data do aprazamento não pode ser retroativa.' }]);
        return;
      }
      if (selected.isSame(today, 'day')) {
        const hour = moment().hour();
        if (nextStepTurno === 'Manhã' && hour >= 12) {
          setErrors([{ message: 'Para a data de hoje, selecione apenas turnos futuros (o turno Manhã já se encerrou).' }]);
          return;
        } else if (nextStepTurno === 'Tarde' && hour >= 18) {
          setErrors([{ message: 'Para a data de hoje, selecione apenas turnos futuros (o turno Tarde já se encerrou).' }]);
          return;
        } else if (hour >= 18) {
          setErrors([{ message: 'Para a data de hoje, nenhum turno futuro está disponível.' }]);
          return;
        }
      }
    } else {
      if (!nextStepMotivoAlta) {
        setErrors([{ message: 'Motivo da Alta é obrigatório.' }]);
        return;
      }
    }

    setIsSubmittingNextStep(true);
    try {
      const service = new NavegacaoService();
      await service.registrarProximaEtapa({
        navegacaoId: id,
        opcao: nextStepOpcao,
        dataAprazamento: nextStepOpcao === 'Aprazamento' ? nextStepData : undefined,
        turnoAprazamento: nextStepOpcao === 'Aprazamento' ? nextStepTurno : undefined,
        motivoAlta: nextStepOpcao === 'Alta' ? nextStepMotivoAlta : undefined
      });
      await loadData();
      navigate(-1);
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] || 'Falha ao registrar próxima etapa.';
      setErrors([{ message: msg }]);
    } finally {
      setIsSubmittingNextStep(false);
    }
  };

  const getValidTurnos = (dateStr: string) => {
    const all = ['Manhã', 'Tarde', 'Noite'];
    if (!dateStr) return all;
    const selected = moment(dateStr, 'YYYY-MM-DD');
    const today = moment().startOf('day');
    if (selected.isSame(today, 'day')) {
      const hour = moment().hour();
      if (hour >= 18) return [];
      if (hour >= 12) return ['Noite'];
      return ['Tarde', 'Noite'];
    }
    return all;
  };

  const validTurnos = getValidTurnos(nextStepData);

  const SectionHeader = ({ title, className }: { title: string; className?: string }) => (
    <div className={className} style={{
      backgroundColor: '#e3f2fd',
      padding: '10px 16px',
      borderRadius: '4px',
      fontWeight: 600,
      fontSize: '1rem',
      color: '#0d47a1',
      marginTop: 25,
      marginBottom: 15
    }}>
      {title}
    </div>
  );

  if (isLoading) {
    return (
      <ContainerWithSidebar>
        <GeneralContainer>
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress color="primary" />
          </Box>
        </GeneralContainer>
      </ContainerWithSidebar>
    );
  }

  if (!navDetails) {
    return (
      <ContainerWithSidebar>
        <GeneralContainer>
          <TextLink onClick={() => navigate(-1)} color={Colors.SPM600}>{'< Voltar'}</TextLink>
          <Spacer top={20} />
          <Typography variant="h6" color="textSecondary" style={{ fontStyle: 'italic' }}>
            Acompanhamento de Navegação não encontrado.
          </Typography>
        </GeneralContainer>
      </ContainerWithSidebar>
    );
  }

  return (
    <ContainerWithSidebar>
      <style>{`
        .print-only {
          display: none !important;
        }
        @media print {
          .print-only {
            display: block !important;
          }
          .no-print {
            display: none !important;
          }
          body, html, #root, 
          .MuiGrid-container, 
          .MuiGrid-container > .MuiGrid-item, 
          .MuiGrid-container > .MuiGrid-item .MuiBox-root {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            position: static !important;
            background-color: white !important;
          }
          .MuiGrid-container > .MuiGrid-item:first-child {
            display: none !important;
          }
          .MuiGrid-container > .MuiGrid-item:last-child {
            width: 100% !important;
            max-width: 100% !important;
            flex-basis: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            background-color: white !important;
          }
        }
      `}</style>
      <GeneralContainer style={{ backgroundColor: '#fff', padding: '30px 40px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <H1 style={{ fontSize: '1.8rem', color: '#333', marginBottom: 5 }}>Painel de Navegação</H1>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 25 }}>
          Acompanhamento clínico de: <strong>{navDetails.pacienteNome}</strong> (CPF: {navDetails.pacienteCPF})
        </Typography>

        <ErrorArea errorList={errors} onCloseAction={() => setErrors([])} />

        <Grid container spacing={3} style={{ marginBottom: 20 }}>
          <Grid item xs={12} sm={4}>
            <H3 style={{ marginBottom: 6 }}>Risco:</H3>
            <div className="no-print">
              <Select
                value={navDetails.risco || ''}
                onChange={(e) => handleAlterarRisco(e.target.value)}
                disabled={!navDetails.ativo}
                fullWidth
                size="small"
                style={{
                  fontWeight: 600,
                  backgroundColor: navDetails.risco === 'Alto' ? '#ffebee' : navDetails.risco === 'Médio' ? '#fff3e0' : '#e8f5e9',
                  color: navDetails.risco === 'Alto' ? '#c62828' : navDetails.risco === 'Médio' ? '#ef6c00' : '#2e7d32'
                }}
              >
                <MenuItem value="Baixo">Baixo</MenuItem>
                <MenuItem value="Médio">Médio</MenuItem>
                <MenuItem value="Alto">Alto</MenuItem>
              </Select>
            </div>
            <div className="print-only" style={{
              fontWeight: 600,
              padding: '6px 12px',
              borderRadius: 4,
              border: '1px solid #ccc',
              backgroundColor: navDetails.risco === 'Alto' ? '#ffebee' : navDetails.risco === 'Médio' ? '#fff3e0' : '#e8f5e9',
              color: navDetails.risco === 'Alto' ? '#c62828' : navDetails.risco === 'Médio' ? '#ef6c00' : '#2e7d32'
            }}>
              {navDetails.risco || 'Sem risco definido'}
            </div>
          </Grid>

          <Grid item xs={12} sm={8}>
            <H3 style={{ marginBottom: 6 }}>Linhas de cuidado:</H3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 40, alignItems: 'center' }}>
              {navDetails.linhasCuidado?.length > 0 ? (
                navDetails.linhasCuidado.map((lc: string, idx: number) => (
                  <span key={idx} style={{ backgroundColor: '#e1f5fe', color: '#0288d1', padding: '4px 10px', borderRadius: 4, fontSize: '0.8rem', fontWeight: 600 }}>
                    {lc}
                  </span>
                ))
              ) : (
                <span style={{ fontSize: '0.9rem', color: '#999', fontStyle: 'italic' }}>Nenhuma linha de cuidado vinculada.</span>
              )}
            </div>
          </Grid>

          <Grid item xs={12}>
            <H3 style={{ marginBottom: 6 }}>Motivo:</H3>
            <div style={{ backgroundColor: '#f9f9f9', padding: 15, borderRadius: 4, border: '1px solid #e0e0e0', fontSize: '0.95rem', color: '#333', whiteSpace: 'pre-wrap' }}>
              {navDetails.motivo || 'Nenhum motivo informado na inclusão.'}
            </div>
          </Grid>
        </Grid>

        {/* Histórico de Risco Section */}
        <SectionHeader title="Histórico de Risco" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {navDetails.riscosHistorico?.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <Table style={{ borderCollapse: 'collapse', width: '100%', backgroundColor: '#fafafa', borderRadius: 4, overflow: 'hidden' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0', backgroundColor: '#f5f5f5', textAlign: 'left' }}>
                    <th style={{ padding: '10px 16px', fontWeight: 600, color: '#555', fontSize: '0.85rem' }}>Data/Hora</th>
                    <th style={{ padding: '10px 16px', fontWeight: 600, color: '#555', fontSize: '0.85rem' }}>Risco</th>
                    <th style={{ padding: '10px 16px', fontWeight: 600, color: '#555', fontSize: '0.85rem' }}>Profissional</th>
                  </tr>
                </thead>
                <tbody>
                  {navDetails.riscosHistorico.map((h: any) => (
                    <tr key={h.id} style={{ borderBottom: '1px solid #e0e0e0', verticalAlign: 'middle' }}>
                      <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#555' }}>{h.data}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          padding: '4px 8px',
                          borderRadius: 4,
                          backgroundColor: h.risco === 'Alto' ? '#ffebee' : h.risco === 'Médio' ? '#fff3e0' : '#e8f5e9',
                          color: h.risco === 'Alto' ? '#c62828' : h.risco === 'Médio' ? '#ef6c00' : '#2e7d32'
                        }}>
                          {h.risco}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#333' }}>{h.profissionalNome}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic', paddingTop: 5, paddingBottom: 5 }}>
              Nenhum histórico de alteração de risco registrado.
            </Typography>
          )}
        </div>

        {/* 1. Evolução Section */}
        <SectionHeader title="Evolução" />
        {navDetails.ativo && (
          <div style={{ marginBottom: 15 }} className="no-print">
            <GenericSolidButton
              onClick={() => setShowAddEvolucao(!showAddEvolucao)}
              style={{ fontSize: '0.9rem', padding: '6px 16px', border: `1px solid ${Colors.SPM600}`, color: Colors.SPM600, backgroundColor: 'white', marginBottom: showAddEvolucao ? 15 : 0 }}
            >
              + Adicionar evolução
            </GenericSolidButton>

            {showAddEvolucao && (
              <div style={{ marginTop: 15 }}>
                <TextareaAutosize
                  minRows={4}
                  placeholder="Digite sobre a evolução..."
                  value={evolucaoText}
                  onChange={(e) => setEvolucaoText(e.target.value)}
                  style={{
                    width: '100%', padding: 12, borderRadius: 4, border: '1px solid #ccc',
                    fontSize: '0.95rem', fontFamily: 'inherit', boxSizing: 'border-box'
                  }}
                />
                <Spacer top={10} />
                <GenericSolidButton
                  disabled={isSubmittingAcomp || !evolucaoText.trim()}
                  onClick={() => handleSalvarAcompanhamento(1)}
                  style={{ width: '100%', maxWidth: 150, backgroundColor: Colors.SPM600, color: 'white', border: 'none' }}
                >
                  Salvar
                </GenericSolidButton>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {navDetails.evolucoes?.length > 0 ? (
            navDetails.evolucoes.map((item: any) => (
              <Card key={item.id} variant="outlined" style={{ borderLeft: '4px solid #0288d1', backgroundColor: '#fafafa' }}>
                <CardContent style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: 5 }}>
                    <strong>{item.data} - {item.hora}</strong> - {item.profissionalNome} {item.profissionalConselho ? `- ${item.profissionalConselho}` : ''} - {item.profissionalCargo}
                  </div>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', color: '#333' }}>
                    {item.texto}
                  </Typography>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic', paddingTop: 5, paddingBottom: 5 }}>
              Nenhuma evolução cadastrada.
            </Typography>
          )}
        </div>

        {/* 2. Plano de Cuidado Section */}
        <SectionHeader title="Plano de Cuidado" />
        {navDetails.ativo && (
          <div style={{ marginBottom: 15 }} className="no-print">
            <GenericSolidButton
              onClick={() => setShowAddPlano(!showAddPlano)}
              style={{ fontSize: '0.9rem', padding: '6px 16px', border: `1px solid ${Colors.SPM600}`, color: Colors.SPM600, backgroundColor: 'white', marginBottom: showAddPlano ? 15 : 0 }}
            >
              + Adicionar Plano de Cuidado
            </GenericSolidButton>

            {showAddPlano && (
              <div style={{ marginTop: 15 }}>
                <TextareaAutosize
                  minRows={4}
                  placeholder="Digite sobre o plano de cuidado..."
                  value={planoText}
                  onChange={(e) => setPlanoText(e.target.value)}
                  style={{
                    width: '100%', padding: 12, borderRadius: 4, border: '1px solid #ccc',
                    fontSize: '0.95rem', fontFamily: 'inherit', boxSizing: 'border-box'
                  }}
                />
                <Spacer top={10} />
                <GenericSolidButton
                  disabled={isSubmittingAcomp || !planoText.trim()}
                  onClick={() => handleSalvarAcompanhamento(2)}
                  style={{ width: '100%', maxWidth: 150, backgroundColor: Colors.SPM600, color: 'white', border: 'none' }}
                >
                  Salvar
                </GenericSolidButton>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {navDetails.planosCuidado?.length > 0 ? (
            navDetails.planosCuidado.map((item: any) => (
              <Card key={item.id} variant="outlined" style={{ borderLeft: '4px solid #0288d1', backgroundColor: '#fafafa' }}>
                <CardContent style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: 5 }}>
                    <strong>{item.data} - {item.hora}</strong> - {item.profissionalNome} {item.profissionalConselho ? `- ${item.profissionalConselho}` : ''} - {item.profissionalCargo}
                  </div>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', color: '#333' }}>
                    {item.texto}
                  </Typography>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic', paddingTop: 5, paddingBottom: 5 }}>
              Nenhum plano de cuidado cadastrado.
            </Typography>
          )}
        </div>

        {/* 3. Condutas e intervenções de Enfermagem Section */}
        <SectionHeader title="Condutas e intervenções" />
        {navDetails.ativo && (
          <div style={{ marginBottom: 15 }} className="no-print">
            <GenericSolidButton
              onClick={() => setShowAddConduta(!showAddConduta)}
              style={{ fontSize: '0.9rem', padding: '6px 16px', border: `1px solid ${Colors.SPM600}`, color: Colors.SPM600, backgroundColor: 'white', marginBottom: showAddConduta ? 15 : 0 }}
            >
              + Adicionar Condutas e intervenções
            </GenericSolidButton>

            {showAddConduta && (
              <div style={{ marginTop: 15 }}>
                <TextareaAutosize
                  minRows={4}
                  placeholder="Digite sobre Condutas e intervenções..."
                  value={condutaText}
                  onChange={(e) => setCondutaText(e.target.value)}
                  style={{
                    width: '100%', padding: 12, borderRadius: 4, border: '1px solid #ccc',
                    fontSize: '0.95rem', fontFamily: 'inherit', boxSizing: 'border-box'
                  }}
                />
                <Spacer top={10} />
                <GenericSolidButton
                  disabled={isSubmittingAcomp || !condutaText.trim()}
                  onClick={() => handleSalvarAcompanhamento(3)}
                  style={{ width: '100%', maxWidth: 150, backgroundColor: Colors.SPM600, color: 'white', border: 'none' }}
                >
                  Salvar
                </GenericSolidButton>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {navDetails.condutasEnfermagem?.length > 0 ? (
            navDetails.condutasEnfermagem.map((item: any) => (
              <Card key={item.id} variant="outlined" style={{ borderLeft: '4px solid #0288d1', backgroundColor: '#fafafa' }}>
                <CardContent style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: 5 }}>
                    <strong>{item.data} - {item.hora}</strong> - {item.profissionalNome} {item.profissionalConselho ? `- ${item.profissionalConselho}` : ''} - {item.profissionalCargo}
                  </div>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', color: '#333' }}>
                    {item.texto}
                  </Typography>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic', paddingTop: 5, paddingBottom: 5 }}>
              Nenhuma conduta ou intervenção cadastrada.
            </Typography>
          )}
        </div>

        {/* 4. Próxima Navegação Section */}
        <SectionHeader title="Próxima navegação" className="no-print" />
        {navDetails.ativo ? (
          <div className="no-print">
            <RadioGroup value={nextStepOpcao} onChange={(e) => setNextStepOpcao(e.target.value)} style={{ flexDirection: 'row', marginBottom: 15 }}>
              <FormControlLabel value="Aprazamento" control={<Radio color="primary" />} label="Aprazamento" />
              <FormControlLabel value="Alta" control={<Radio color="primary" />} label="Alta" />
            </RadioGroup>

            {nextStepOpcao === 'Aprazamento' ? (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <H3 style={{ marginBottom: 6 }}>Data:</H3>
                  <TextField
                    type="date"
                    value={nextStepData}
                    onChange={(e) => {
                      setNextStepData(e.target.value);
                      setNextStepTurno('');
                    }}
                    fullWidth
                    size="small"
                    inputProps={{ min: moment().format('YYYY-MM-DD') }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <H3 style={{ marginBottom: 6 }}>Turno:</H3>
                  <Select
                    value={nextStepTurno}
                    onChange={(e) => setNextStepTurno(e.target.value)}
                    fullWidth
                    size="small"
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Selecione</MenuItem>
                    {validTurnos.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <H3 style={{ marginBottom: 6 }}>Motivo da Alta:</H3>
                  <Select
                    value={nextStepMotivoAlta}
                    onChange={(e) => setNextStepMotivoAlta(e.target.value)}
                    fullWidth
                    size="small"
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Selecione</MenuItem>
                    <MenuItem value="Melhorado">Melhorado</MenuItem>
                    <MenuItem value="Falta de adesão">Falta de adesão</MenuItem>
                    <MenuItem value="Desligamento">Desligamento</MenuItem>
                    <MenuItem value="Óbito">Óbito</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            )}
          </div>
        ) : (
          <div style={{ padding: '15px 20px', backgroundColor: '#fff3e0', border: '1px solid #ffe0b2', color: '#e65100', borderRadius: 4, fontWeight: 500 }} className="no-print">
            Este processo de navegação já foi encerrado e recebeu Alta.
          </div>
        )}

        {/* Bottom Action Bar */}
        <Spacer top={40} />
        <div style={{ display: 'flex', gap: 15, justifyContent: 'flex-end' }} className="no-print">
          <GenericSolidButton
            onClick={() => navigate(-1)}
            style={{ minWidth: 120, border: `1px solid ${Colors.SPM600}`, color: Colors.SPM600, backgroundColor: 'white' }}
          >
            {navDetails?.ativo ? 'Cancelar' : 'Voltar'}
          </GenericSolidButton>

          <GenericSolidButton
            onClick={() => window.print()}
            style={{ minWidth: 120, backgroundColor: Colors.SPM600, color: 'white', border: 'none' }}
          >
            Imprimir
          </GenericSolidButton>

          {navDetails.ativo && (
            <>
              <GenericSolidButton
                disabled={isSubmittingNextStep}
                onClick={() => {
                  setNextStepOpcao('Alta');
                  setNextStepMotivoAlta('Melhorado');
                  // Trigger finalization immediately using default values
                  setTimeout(() => {
                    handleSalvarProximaEtapa();
                  }, 100);
                }}
                style={{ minWidth: 120, backgroundColor: Colors.SPM600, color: 'white', border: 'none' }}
              >
                Finalizar
              </GenericSolidButton>
            </>
          )}
        </div>
      </GeneralContainer>
    </ContainerWithSidebar>
  );
};

export default AcompanhamentoNavegacao;
